import type { BootConfig } from '../config';
import {
  type ChoiceEntry,
  type ContentPack,
  type Dir,
  type EndReason,
  type EngineState,
  type InputEntry,
  type Question,
  type ReviveResult,
  type ReviveView,
  type Runner,
  type WordOutcome,
  angerStage,
  createRunner,
  deriveRunSeed,
  isOver,
  logicalLength,
  tickMs,
  toReviveView,
} from '../engine';
import { isOpposite } from '../engine';
import { type DirQueue, createQueue, popDir, pushDir } from '../input/queue';
import { type KnownMap, knownOf } from '../selfreport/pre-run';
import type { Telemetry } from '../telemetry';
import type { Effect } from '../view/renderer';
import { type SealView, deriveSealView, extrasFromState } from '../view/seal';
import { pushEffect, ringEffects } from './effects';

/**
 * drain() 이 만드는 유일한 출력. 이펙트와 사운드가 같은 버스를 타야 캐치업 프레임(최대 3틱)에서
 * 화면과 소리가 1프레임 어긋나지 않는다(UPLIFT §6.1). 뷰가 프레임마다 비운다.
 */
export type Cue =
  /** combo = 이 단어에서 먹은 글자 순번(1부터). 점수 콤보는 폐지됐고(SEAL §2) 사다리 음정 입력만 남았다. */
  | { readonly k: 'eat_good'; readonly combo: number }
  | { readonly k: 'eat_bad'; readonly atFloor: boolean }
  | { readonly k: 'word_end'; readonly outcome: WordOutcome; readonly perfect: boolean }
  | { readonly k: 'relocate' }
  | { readonly k: 'penalty_on' }
  | { readonly k: 'penalty_off' }
  | { readonly k: 'over'; readonly reason: EndReason }
  | { readonly k: 'revive_open' }
  | { readonly k: 'revive_judge'; readonly result: Exclude<ReviveResult, 'none'> }
  | { readonly k: 'revive_done'; readonly used: boolean }
  /** READY 카운트다운 pip(3·2·1) — 밴드가 그리고 countdown 음이 울린다 */
  | { readonly k: 'ready'; readonly pip: number }
  | { readonly k: 'go' };

export interface SessionDeps {
  readonly cfg: BootConfig;
  readonly pack: ContentPack;
  readonly tlm: Telemetry;
  readonly known: KnownMap | null;
  readonly runIndex: number;
  readonly gameIndex: number; // 판 번호 1부터
}

export interface Session {
  runner: Runner;
  queue: DirQueue;
  inputLog: InputEntry[];
  prev: EngineState;
  effects: Effect[];
  shake: number;
  /** 이번 프레임에 쌓인 큐 — 뷰가 재생하고 비운다 */
  cues: Cue[];
  overLatched: boolean;
  hiddenMs: number;
  hiddenCount: number;
  /** 현재 READY 구간의 총 틱(판 시작 11 · 부활 6) — pip 분모 */
  readyTotal: number;
  /** READY 중 큐가 같은 방향이라 버린 입력 — 그래도 READY 는 건너뛴다(READY_SKIP_ON_INPUT) */
  readyKick: boolean;
  readonly deps: SessionDeps;
  /** 부활 선택 — 다음 틱에 엔진으로 들어간다(방향 큐와 같은 규약). */
  choice: number | null;
  choiceLog: ChoiceEntry[];
  question(): Question;
  pushInput(d: Dir): boolean;
  pickChoice(i: number): void;
  /** 부활 페이즈가 아니면 null. 판정 전에는 정답 위치가 없다(toReviveView). */
  reviveView(): ReviveView | null;
  advance(): void;
  effectiveTickMs(): number;
  /** 프레젠테이션 뷰모델 — 성장 시계·길이 원장은 엔진 상태에서 직접 읽는다. */
  seal(): SealView;
  forceEnd(reason: 'quit' | 'abandoned'): void;
}

export function createSession(deps: SessionDeps): Session {
  const { cfg, pack, tlm, known } = deps;
  // 판마다 시드를 파생한다. 세션 시드를 그대로 쓰면 layout/decoy/respawn 스트림이 매 판
  // 같은 초기값에서 출발해 판이 거듭돼도 타일 좌표가 완전히 같고, 2판째부터 기하 암기가
  // 지식을 대체해 우회율이 지식을 재지 못한다.
  const runner = createRunner(deriveRunSeed(cfg.seed, deps.runIndex), pack, cfg.engineRules, {
    sessionSeed: cfg.seed,
    runIndex: deps.runIndex,
  });
  const byId = new Map(pack.questions.map((q) => [q.id, q]));

  const s: Session = {
    runner,
    queue: createQueue(runner.state.snake.dir),
    inputLog: [],
    prev: runner.state,
    effects: [],
    shake: 0,
    cues: runner.state.readyTicksLeft > 0 ? [{ k: 'ready', pip: 3 }] : [],
    overLatched: false,
    hiddenMs: 0,
    hiddenCount: 0,
    readyTotal: runner.state.readyTicksLeft,
    readyKick: false,
    deps,
    choice: null,
    choiceLog: [],
    question: () => byId.get(runner.state.word.questionId) as Question,
    pickChoice(i) {
      if (runner.state.phase === 'revive' && runner.state.revive.status === 1) s.choice = i;
    },
    reviveView() {
      const st = runner.state;
      if (st.phase !== 'revive') return null;
      const q = byId.get(st.revive.questionId);
      return q === undefined ? null : toReviveView(st, q, cfg.engineRules);
    },
    pushInput(d) {
      const before = s.queue;
      s.queue = pushDir(s.queue, d, cfg.viewRules.dirQueueMax);
      // 같은 방향 스와이프도 '첫 입력'이다 — 큐는 버리지만 READY 는 이 틱에 끝낸다
      if (
        s.queue === before &&
        runner.state.readyTicksLeft > 0 &&
        !isOpposite(runner.state.snake.dir, d)
      )
        s.readyKick = true;
      return s.queue !== before;
    },
    advance() {
      if (isOver(runner.state)) return;
      // 부활 페이즈 — 방향은 무시하고 선택만 넘긴다. 큐는 revive_offer 에서 비웠다.
      if (runner.state.phase === 'revive') {
        const c = s.choice;
        s.choice = null;
        const t = runner.state.tick + 1;
        if (c !== null) s.choiceLog.push({ t, c });
        s.prev = runner.state;
        runner.tickOnce(null, c);
        drain(s);
        return;
      }
      // WORD_CLEAR 정지 구간에서는 큐를 건드리지 않는다 — 엔진이 어차피 소비하지 않으므로
      // 여기서 뽑으면 ㄱ자 선입력이 조용히 증발해 "스와이프가 씹힌다"가 된다.
      if (runner.state.clearTicksLeft > 0) {
        s.prev = runner.state;
        runner.tickOnce(null);
        drain(s);
        return;
      }
      const popped = popDir(s.queue);
      s.queue = popped.queue;
      let dir = popped.dir;
      if (dir === null && s.readyKick && runner.state.readyTicksLeft > 0)
        dir = runner.state.snake.dir;
      s.readyKick = false;
      const t = runner.state.tick + 1;
      if (dir !== null) s.inputLog.push({ t, d: dir });
      s.prev = runner.state;
      runner.tickOnce(dir);
      drain(s);
    },
    effectiveTickMs: () => tickMs(runner.state, runner.ctx),
    seal: () =>
      deriveSealView(
        runner.state,
        extrasFromState(runner.state, cfg.engineRules),
        cfg.engineRules.runWordCount,
      ),
    forceEnd(reason) {
      if (s.overLatched) return;
      s.overLatched = true;
      emitGameOver(s, reason);
    },
  };

  /**
   * 섭취 이펙트는 상한(EFFECT_MAX)에 걸리면 오래된 섭취 이펙트부터 밀려나고, 밀어낼 것이 링뿐이면
   * 링이 전량 사라진다 — 링 16장 중 한 장(= 다음 정답 타일)만 빠지는 경로를 없앤다(SEAL §5.E).
   */
  function pushFx(sess: Session, fx: Effect): void {
    sess.effects = pushEffect(sess.effects, fx);
  }

  function drain(sess: Session): void {
    const st = sess.runner.state;
    for (const e of st.events) {
      switch (e.kind) {
        case 'eat_correct':
          pushFx(sess, { kind: 'good', cell: e.cell, ttl: 12 });
          sess.cues.push({ k: 'eat_good', combo: st.word.eaten });
          break;
        case 'eat_wrong': {
          pushFx(sess, { kind: 'bad', cell: e.cell, ttl: 12 });
          sess.shake = 6;
          sess.cues.push({ k: 'eat_bad', atFloor: e.atFloor });
          tlm.track('answer_wrong', {
            gi: deps.gameIndex,
            runIndex: deps.runIndex,
            sessionSeed: cfg.seed,
            wi: st.word.index,
            q: st.word.questionId,
            tk: st.tick,
            dt: st.tick - st.word.spawnTick,
            ch: e.ch,
            slot: e.slot,
            ci: st.word.eaten,
            jsim: e.jamoSim,
            len: logicalLength(st),
            flr: e.atFloor,
            pen: st.penalty.status,
            first: e.firstHit,
            k: knownOf(known, st.word.questionId),
          });
          break;
        }
        case 'relocate':
          // 재배치 링은 **전 타일에 전부 아니면 전무**다. FIFO 상한에 걸려 일부만 남으면
          // "링이 붙은 집합에 정답이 있다"가 되어 ADR-008 이 이펙트 큐 뒷문으로 깨진다.
          // 그래서 상한을 적용하는 대신 큐를 통째로 갈아끼운다(그 프레임의 콜 수도 16으로 묶인다).
          // 이후 섭취 이펙트가 들어와 상한을 넘으면 pushEffect 가 링을 전량 폐기한다(부분 제거 없음).
          sess.effects = ringEffects(
            st.tiles.map((t) => t.cell),
            12,
          );
          sess.cues.push({ k: 'relocate' });
          break;
        case 'word_end': {
          const r = e.record;
          // 단어 종료 연출 — WORD_CLEAR 6틱 안에 끝난다(뱀 정지·타일 제거 구간에서만)
          {
            const head = st.snake.cells[0] as number;
            if (r.outcome === 'solved') {
              pushFx(sess, { kind: 'bang', cell: head, ttl: 6 });
              if (r.perfect) pushFx(sess, { kind: 'burst', cell: head, ttl: 6 });
            } else if (r.outcome === 'skipped') pushFx(sess, { kind: 'aigo', cell: head, ttl: 6 });
          }
          tlm.track('word_end', {
            gi: deps.gameIndex,
            runIndex: deps.runIndex,
            sessionSeed: cfg.seed,
            wi: r.index,
            q: r.questionId,
            L: r.answerLen,
            outcome: r.outcome,
            sh: r.showTick,
            st: r.spawnTick,
            et: r.endTick,
            mv: r.movedTicks,
            id: r.idealTicks,
            dt: r.endTick - r.spawnTick,
            segMv: r.segMoved,
            segId: r.segIdeal,
            w: r.wrongEats,
            dw: r.decoyEats,
            ow: r.outOfOrderEats,
            jsim: r.wrongJamoSim,
            ftt: r.firstTurnTick,
            rel: r.relocations,
            rvw: r.reviveInWord ? 1 : 0, // 부활이 끼어든 문제 — 우회율 분석에서 걷어내는 표식
            // 길이 경제(SEAL §7) — 단어 시작/끝 논리 길이, 시간·오답으로 자란 칸 수
            len0: r.len0,
            len1: r.len1,
            gr: r.autoGrown,
            wg: r.wrongGrown,
            perfect: r.perfect,
            nom: r.nominalMs,
            k: knownOf(known, r.questionId),
            hs: angerStage(r.wordTick, cfg.engineRules), // 종료 시 분노 단계 (RULES §1.16)
            wt: r.wordTick,
          });
          sess.cues.push({ k: 'word_end', outcome: r.outcome, perfect: r.perfect });
          break;
        }
        case 'revive_offer':
          sess.queue = createQueue(sess.runner.state.snake.dir); // 선입력이 부활 뒤로 새지 않게
          sess.choice = null;
          sess.cues.push({ k: 'revive_open' });
          break;
        case 'revive_judge':
          sess.cues.push({ k: 'revive_judge', result: e.result });
          break;
        case 'revive_done':
          sess.queue = createQueue(sess.runner.state.snake.dir);
          sess.prev = sess.runner.state; // 이 한 틱만 보간 무력화 — 뱀이 화면을 가로질러 미끄러지지 않게
          sess.effects = [];
          sess.cues.push({ k: 'revive_done', used: e.used });
          break;
        case 'game_over':
          if (!sess.overLatched) {
            sess.overLatched = true;
            emitGameOver(sess, e.reason);
          }
          sess.cues.push({ k: 'over', reason: e.reason });
          break;
        default:
          break;
      }
    }
    // READY 카운트다운 — 판 시작(11틱)과 부활 후(6틱) 둘 다. pip = ceil(left / total × 3)
    const prevLeft = sess.prev.readyTicksLeft;
    const curLeft = st.readyTicksLeft;
    if (curLeft > prevLeft) sess.readyTotal = curLeft; // 새 READY 구간 진입
    const pipOf = (left: number): number =>
      left > 0 && sess.readyTotal > 0 ? Math.ceil((left / sess.readyTotal) * 3) : 0;
    const p1 = pipOf(curLeft);
    if (p1 > 0 && p1 !== pipOf(prevLeft)) sess.cues.push({ k: 'ready', pip: p1 });
    if (prevLeft > 0 && curLeft === 0 && st.phase === 'play') sess.cues.push({ k: 'go' });

    // 페널티 드론은 status(벽 근처에서 초당 4~8회 진동)가 아니라 **에피소드**(ticksLeft > 0) 경계로 켜고 끈다
    const wasEp = sess.prev.penalty.ticksLeft > 0;
    const isEp = st.penalty.ticksLeft > 0;
    if (!wasEp && isEp) sess.cues.push({ k: 'penalty_on' });
    if (wasEp && !isEp) sess.cues.push({ k: 'penalty_off' });

    for (const fx of sess.effects) fx.ttl--;
    sess.effects = sess.effects.filter((f) => f.ttl > 0);
    if (sess.shake > 0) sess.shake--;
  }

  function emitGameOver(sess: Session, reason: string): void {
    const st = sess.runner.state;
    const res = sess.runner.result();
    // 다음 자동 성장까지 남은 이동 틱(SEAL §7 K6 입력). word.index 는 판이 끝나도 마지막 문제에
    // 머무르므로(spawnNextWord 가 상한에서 먼저 반환) 테이블 밖을 읽지 않지만, 방어적으로 0 폴백.
    const growPeriod = sess.runner.ctx.rules.growPeriodTable[st.word.index] ?? 0;
    tlm.track(
      'game_over',
      {
        gi: deps.gameIndex,
        rs: reason,
        tk: st.tick,
        nom: st.nominalMs,
        hiddenMs: Math.round(sess.hiddenMs),
        hiddenCount: sess.hiddenCount,
        len: logicalLength(st),
        pk: st.peakLength,
        // 순위 키(SEAL §2 RANK_KEY) 세 항 + 길이 경제 요약
        sealed: res.sealed,
        wc: res.solved,
        lst: res.lastSolveTick,
        qn: st.records.length,
        wr: res.wrongEats,
        gr: res.autoGrown,
        // `mt` 는 메타(단조 시계)가 쓴다 — payload 가 그 이름을 쓰면 track() 이 메타를 우선해 버린다
        mvt: res.moveTicks,
        gtn: Math.max(0, growPeriod - st.growTicks),
        sd: res.spawnDegraded,
        sr: res.spawnRelaxed,
        rvo: res.reviveOffered,
        rvu: res.reviveUsed,
        rvr: res.reviveResult,
        rvq: res.reviveQuestionId,
        rva: res.reviveAnswerTicks,
        rvt: res.reviveTicks, // 판정 도구가 tk 에서 뺀다(부활 페이즈가 최대 119틱을 얹는다)
        sum: res.checksum,
        runIndex: deps.runIndex,
        sessionSeed: cfg.seed,
        seed: st.seed,
      },
      true,
    );
  }

  return s;
}
